// # 입력 표기 ↔ 본문 블록
//
// 입력칸은 글자다. 서식 단추·단축키는 선택한 글에 **표기를 두른다**(슬랙 mrkdwn 과 같은
// 기호: `*굵게*` `_기울임_` `~취소선~` `` `코드` ``, 줄 머리 `> ` `- ` `1. `, 울타리 ```` ``` ````).
// 보낼 때 이 파일이 블록으로 바꾼다. 서버는 블록만 받으므로 **파서는 여기 하나뿐**이다.
//
// 고칠 때는 거꾸로 `markup_of` 가 블록을 같은 표기로 되돌린다 — 평문(`text_of`)으로
// 되돌리면 고치기 한 번에 서식이 전부 사라진다.

import type { Block, GroupId, Span, UserId } from './index.ts'

type Flags = { bold: boolean; italic: boolean; strike: boolean; code: boolean }
type TextSpan = Extract<Span, { t: 'text' }>

const NONE: Flags = { bold: false, italic: false, strike: false, code: false }

/**
 * 짝 기호. 여는 기호 앞은 줄 머리·공백·여는 괄호, 닫는 기호 뒤는 줄 끝·공백·문장부호여야 한다.
 * 그래야 `snake_case_name` 이나 `2*3*4` 가 서식으로 먹히지 않는다. 다만 ASCII 밖의 글자(한글)는
 * 양쪽 모두 경계로 친다 — `*배포*가` 처럼 조사가 바로 붙는 것이 한국어의 보통 문장이라, 이것을
 * 막으면 굵게 한 낱말 뒤에 억지로 띄어 써야 했다.
 */
const PAIRS: readonly { mark: string; flag: keyof Flags }[] = [
  // 마크다운 버릇(`**굵게**`·`~~취소~~`)도 받는다. 같은 자리에서는 앞의 것(두 겹)이 이긴다.
  { mark: '**', flag: 'bold' },
  { mark: '~~', flag: 'strike' },
  { mark: '*', flag: 'bold' },
  { mark: '_', flag: 'italic' },
  { mark: '~', flag: 'strike' },
]

function escape_mark(mark: string): string {
  return mark.replace(/[*~]/g, '\\$&')
}

/** ASCII 밖의 글자. 조사·어미가 기호에 바로 붙어도 서식이 먹게 한다. */
const WIDE = '[^\\x00-\\x7F]'

function pair_re(mark: string): RegExp {
  const m = escape_mark(mark)
  return new RegExp(
    `(^|[\\s(\\[]|${WIDE})${m}([^\\s${m}](?:[^${m}\\n]*[^\\s${m}])?)${m}(?=$|[\\s.,!?;:)\\]'"]|${WIDE})`,
  )
}

const CODE_RE = /`([^`\n]+)`/

/**
 * 입력칸 거울의 조각. 기호(`mark`)도 글자 그대로 남아 이어 붙이면 **원문과 같다** — 자리를 잃지
 * 않아야 입력칸 뒤에 겹쳐 그릴 수 있다. 보낼 때의 `inline_of` 도 이것에서 기호만 뺀 것이라
 * 화면이 보여 주는 서식과 보내는 서식이 갈리지 않는다.
 */
export type MarkPiece = { text: string; mark: boolean } & Flags

function pieces_of(text: string, flags: Flags): MarkPiece[] {
  const out: MarkPiece[] = []
  let rest = text
  while (rest.length > 0) {
    let best: { at: number; len: number; inner: string; flag: keyof Flags; sign: string } | null =
      null
    const code = CODE_RE.exec(rest)
    if (code !== null)
      best = { at: code.index, len: code[0].length, inner: code[1] ?? '', flag: 'code', sign: '`' }
    for (const p of PAIRS) {
      const m = pair_re(p.mark).exec(rest)
      if (m === null) continue
      const lead = m[1]?.length ?? 0
      const at = m.index + lead
      if (best === null || at < best.at) {
        best = { at, len: m[0].length - lead, inner: m[2] ?? '', flag: p.flag, sign: p.mark }
      }
    }
    if (best === null) {
      out.push({ text: rest, mark: false, ...flags })
      break
    }
    if (best.at > 0) out.push({ text: rest.slice(0, best.at), mark: false, ...flags })
    const { sign } = best
    out.push({ text: sign, mark: true, ...flags })
    if (best.flag === 'code') out.push({ text: best.inner, mark: false, ...flags, code: true })
    else out.push(...pieces_of(best.inner, { ...flags, [best.flag]: true }))
    out.push({ text: sign, mark: true, ...flags })
    rest = rest.slice(best.at + best.len)
  }
  return out
}

/** 한 줄 안의 서식 기호를 조각으로. 코드 조각 안은 더 풀지 않는다. */
export function inline_of(text: string, flags: Flags = NONE): TextSpan[] {
  return merge(
    pieces_of(text, flags)
      .filter((p) => !p.mark)
      .map(({ text, bold, italic, strike, code }) => ({
        t: 'text',
        text,
        bold,
        italic,
        strike,
        code,
      })),
  )
}

/** 같은 서식의 이웃 조각을 붙인다. */
function merge(spans: TextSpan[]): TextSpan[] {
  const out: TextSpan[] = []
  for (const s of spans) {
    const last = out.at(-1)
    if (
      last !== undefined &&
      last.bold === s.bold &&
      last.italic === s.italic &&
      last.strike === s.strike &&
      last.code === s.code
    ) {
      out[out.length - 1] = { ...last, text: last.text + s.text }
    } else out.push(s)
  }
  return out
}

const BULLET = /^\s*[-*•]\s+(.*)$/
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/
const QUOTE = /^>\s?(.*)$/
const FENCE = /^```(.*)$/

/** 한 덩이의 글줄 → 서식 조각. 줄 안 처리(링크·멘션)는 부르는 쪽이 `line` 으로 한다. */
type Line = (text: string) => Span[]

/** 줄 묶음 하나를 읽은 결과: 만든 블록(없을 수 있다)과 다음 줄 번호. */
type Took = { block: Block | null; next: number }

/** 울타리 코드 블록. 한 줄 울타리(```코드```)도, 닫지 않은 울타리(끝까지)도 받는다. */
function take_fence(lines: readonly string[], i: number, lang_raw: string): Took {
  const one = /^```(.+)```$/.exec((lines[i] ?? '').trim())
  if (one !== null) return { block: { t: 'code', lang: null, code: one[1] ?? '' }, next: i + 1 }
  const body: string[] = []
  let j = i + 1
  while (j < lines.length && (lines[j] ?? '').trim() !== '```') {
    body.push(lines[j] ?? '')
    j += 1
  }
  const code = body.join('\n')
  const lang = lang_raw.trim()
  const block: Block | null =
    code.trim().length > 0 ? { t: 'code', lang: lang === '' ? null : lang, code } : null
  return { block, next: j + 1 }
}

/** 같은 머리 기호가 이어지는 줄들의 알맹이. */
function run_of(lines: readonly string[], i: number, re: RegExp): { got: string[]; next: number } {
  const got: string[] = []
  let j = i
  while (j < lines.length && re.test(lines[j] ?? '')) {
    got.push(re.exec(lines[j] ?? '')?.[1] ?? '')
    j += 1
  }
  return { got, next: j }
}

function take_quote(lines: readonly string[], i: number, line: Line): Took {
  const { got, next } = run_of(lines, i, QUOTE)
  const text = got.join('\n').trim()
  return { block: text.length > 0 ? { t: 'quote', spans: line(text) } : null, next }
}

function take_list(lines: readonly string[], i: number, kind: RegExp, line: Line): Took {
  const { got, next } = run_of(lines, i, kind)
  const entries = got
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .map(line)
  const block: Block | null =
    entries.length > 0 ? { t: 'list', ordered: kind === NUMBERED, entries } : null
  return { block, next }
}

/** 이 줄에서 시작하는 특별한 묶음(울타리·인용·목록)을 읽는다. 평범한 줄이면 `null`. */
function take_special(lines: readonly string[], i: number, line: Line): Took | null {
  const raw = lines[i] ?? ''
  const fence = FENCE.exec(raw.trim())
  if (fence !== null) return take_fence(lines, i, fence[1] ?? '')
  if (QUOTE.test(raw)) return take_quote(lines, i, line)
  if (BULLET.test(raw)) return take_list(lines, i, BULLET, line)
  if (NUMBERED.test(raw)) return take_list(lines, i, NUMBERED, line)
  return null
}

/**
 * 표기를 블록으로. 빈 줄은 문단을 가르고, 줄 머리 기호는 인용·목록이 되고,
 * 울타리는 코드 블록이 된다(안은 글자 그대로). 울타리를 닫지 않으면 끝까지 코드다.
 */
export function blocks_of(written: string, line: Line): Block[] {
  const lines = written.split('\n')
  const out: Block[] = []
  let para: string[] = []
  const flush = () => {
    const text = para.join('\n').trim()
    if (text.length > 0) out.push({ t: 'para', spans: line(text) })
    para = []
  }
  let i = 0
  while (i < lines.length) {
    const took = take_special(lines, i, line)
    if (took !== null) {
      flush()
      if (took.block !== null) out.push(took.block)
      i = took.next
      continue
    }
    const raw = lines[i] ?? ''
    if (raw.trim() === '') flush()
    else para.push(raw)
    i += 1
  }
  flush()
  return out
}

/** 거울의 한 줄. `code` 는 울타리 안, `fence` 는 울타리 줄 자체다. */
export type MarkLine = {
  kind: 'plain' | 'quote' | 'bullet' | 'numbered' | 'fence' | 'code'
  pieces: MarkPiece[]
}

/**
 * 입력칸 글 → 줄마다 서식 조각. `blocks_of` 와 같은 정규식으로 줄 머리를 가르고, 줄 안은
 * `inline_of` 와 같은 규칙이다. 줄 수와 글자는 원문 그대로다(빈 줄은 조각이 없다).
 */
export function marks_of(written: string): MarkLine[] {
  const out: MarkLine[] = []
  let fenced = false
  for (const raw of written.split('\n')) {
    const bare = raw.trim()
    const piece = (text: string, mark: boolean): MarkPiece[] =>
      text === '' ? [] : [{ text, mark, ...NONE }]
    // 여는 줄은 ```말, 닫는 줄은 ``` 하나뿐이다(`take_fence` 와 같다). 한 줄 울타리는 열지 않는다.
    const edge = fenced ? bare === '```' : FENCE.test(bare)
    if (fenced || edge) {
      if (edge && (fenced || !/^```(.+)```$/.test(bare))) fenced = !fenced
      out.push({ kind: edge ? 'fence' : 'code', pieces: piece(raw, edge) })
      continue
    }
    const head = (
      [
        ['quote', QUOTE],
        ['bullet', BULLET],
        ['numbered', NUMBERED],
      ] as const
    ).find(([, re]) => re.test(raw))
    if (head === undefined) {
      out.push({ kind: 'plain', pieces: pieces_of(raw, NONE) })
      continue
    }
    const inner = head[1].exec(raw)?.[1] ?? ''
    const lead = raw.slice(0, raw.length - inner.length)
    out.push({ kind: head[0], pieces: [...piece(lead, true), ...pieces_of(inner, NONE)] })
  }
  return out
}

// ── 되돌리기 ───────────────────────────────────────────────

/** 블록 → 입력칸에 다시 넣을 표기. `blocks_of` 의 역이다(첨부·구분선은 뺀다). */
/**
 * 고칠 때 호출을 `@이름` 으로 펴는 손. 없거나 모르는 이름이면 번호(`@3`)로 남는다 — 이름을 모르면
 * 번호밖에 가리킬 것이 없다. 되돌리는 쪽은 `@maru/ui` 의 `names_resolve` 다(고칠 때만 받는 청크).
 */
export interface MarkupNames {
  user?: ((id: UserId) => string | undefined) | undefined
  group?: ((id: GroupId) => string | undefined) | undefined
}

export function markup_of(body: readonly Block[], names?: MarkupNames): string {
  return body
    .map((b) => block_markup(b, names))
    .filter((s) => s.length > 0)
    .join('\n\n')
}

function block_markup(b: Block, names: MarkupNames | undefined): string {
  switch (b.t) {
    case 'para':
      return spans_markup(b.spans, names)
    case 'quote':
      return spans_markup(b.spans, names)
        .split('\n')
        .map((l) => `> ${l}`)
        .join('\n')
    case 'list':
      return b.entries
        .map((e, i) => `${b.ordered ? `${i + 1}.` : '-'} ${spans_markup(e, names)}`)
        .join('\n')
    case 'code':
      return `\`\`\`${b.lang ?? ''}\n${b.code}\n\`\`\``
    case 'header':
      return b.text
    case 'section':
      return [b.spans, ...(b.fields ?? [])].map((x) => spans_markup(x, names)).join('\n')
    case 'context':
      return spans_markup(b.spans, names)
    case 'divider':
    case 'attachment':
    case 'image':
    case 'actions':
      return ''
  }
}

function spans_markup(spans: readonly Span[], names: MarkupNames | undefined): string {
  return spans.map((s) => span_markup(s, names)).join('')
}

/** 이름이 `@이름` 으로 다시 읽힐 모양인가 — 공백·`@` 가 들면 호출로 되돌아오지 않는다. */
const callable = (name: string | undefined): name is string =>
  name !== undefined && name.length > 0 && !/[\s@]/.test(name)

function span_markup(s: Span, names: MarkupNames | undefined): string {
  switch (s.t) {
    case 'text': {
      if (s.code) return `\`${s.text}\``
      let t = s.text
      if (s.strike) t = `~${t}~`
      if (s.italic) t = `_${t}_`
      if (s.bold) t = `*${t}*`
      return t
    }
    case 'mention_user': {
      const name = names?.user?.(s.user)
      return `@${callable(name) ? name : s.user}`
    }
    case 'mention_group': {
      const name = names?.group?.(s.group)
      return `@${callable(name) ? name : s.group}`
    }
    case 'mention_all':
      return s.here ? '@here' : '@channel'
    case 'link':
      return s.view == null || s.view === s.url ? s.url : `<${s.url}|${s.view}>`
    case 'emoji':
      return `:${s.name}:`
  }
}

// ── 입력칸 편집(서식 단추·단축키) ────────────────────────────

/** 입력칸의 글과 선택 구간. 서식 동작은 새 글과 새 선택을 돌려준다. */
export interface Edit {
  text: string
  start: number
  end: number
}

export type Format =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'code'
  | 'code_block'
  | 'quote'
  | 'bullet'
  | 'numbered'

const WRAP: Partial<Record<Format, string>> = { bold: '*', italic: '_', strike: '~', code: '`' }

/**
 * 선택한 글에 서식을 두른다. 이미 둘러져 있으면 벗긴다(토글).
 * 줄 서식(인용·목록)은 선택이 걸친 **모든 줄**의 머리에 붙인다.
 */
export function apply_format(e: Edit, f: Format): Edit {
  const mark = WRAP[f]
  if (mark !== undefined) return wrap(e, mark)
  if (f === 'code_block') return fence(e)
  return prefix_lines(e, f)
}

/** 벗길 때 함께 알아보는 기호 — 마크다운 버릇의 두 겹(`**`·`~~`)도 같은 서식이다. */
const ALIKE: Readonly<Record<string, readonly string[]>> = {
  '*': ['**', '*'],
  '~': ['~~', '~'],
  _: ['_'],
  '`': ['`'],
}

/** 한 줄에서 `col` 을 감싼 짝의 [여는 자리, 닫는 기호 뒤, 기호 길이]. 없으면 `null`. */
function pair_around(line: string, col: number, mark: string): [number, number, number] | null {
  for (const sign of ALIKE[mark] ?? [mark]) {
    const re =
      sign === '`' ? new RegExp(CODE_RE.source, 'g') : new RegExp(pair_re(sign).source, 'g')
    for (let m = re.exec(line); m !== null; m = re.exec(line)) {
      const lead = sign === '`' ? 0 : (m[1]?.length ?? 0)
      const open = m.index + lead
      const close = m.index + m[0].length
      if (col >= open + sign.length && col <= close - sign.length) return [open, close, sign.length]
      // 여는 자리 앞 글자(경계)를 먹었으니 다음 찾기는 그 글자 뒤에서 — 이웃한 짝을 놓치지 않는다.
      re.lastIndex = Math.max(open + 1, close - sign.length)
    }
  }
  return null
}

/** 둘러져 있는가 — 알맹이가 있는 짝 기호로 시작해 끝난다. 둘러진 기호 길이, 아니면 0. */
function wrapped_by(s: string, mark: string): number {
  for (const sign of ALIKE[mark] ?? [mark]) {
    if (s.length > sign.length * 2 && s.startsWith(sign) && s.endsWith(sign)) return sign.length
  }
  return 0
}

function wrap(e: Edit, mark: string): Edit {
  const { text } = e
  const n = mark.length
  // 끌어 고를 때 딸려 온 앞뒤 공백은 두르지 않는다 — `*굵게 *` 는 서식이 되지 않는다.
  let start = e.start
  let end = e.end
  while (start < end && /\s/.test(text[start] ?? '')) start++
  while (end > start && /\s/.test(text[end - 1] ?? '')) end--
  if (e.start !== e.end && start === end) return e
  const picked = text.slice(start, end)
  if (picked.includes('\n')) return wrap_lines(text, start, end, mark)
  // 선택 바깥에 기호가 붙어 있으면 벗긴다.
  for (const sign of ALIKE[mark] ?? [mark]) {
    const k = sign.length
    if (text.slice(start - k, start) === sign && text.slice(end, end + k) === sign) {
      return {
        text: text.slice(0, start - k) + picked + text.slice(end + k),
        start: start - k,
        end: end - k,
      }
    }
  }
  // 기호째 골랐으면 벗긴다 — 한 겹 더 두르면 `**굵게**` 가 `*` 넷이 된다.
  const k = wrapped_by(picked, mark)
  if (k > 0) {
    const inner = picked.slice(k, -k)
    return {
      text: text.slice(0, start) + inner + text.slice(end),
      start,
      end: start + inner.length,
    }
  }
  const head = text.lastIndexOf('\n', start - 1) + 1
  const tail_at = text.indexOf('\n', end)
  const line = text.slice(head, tail_at < 0 ? text.length : tail_at)
  if (start === end) {
    // 선택 없이 닫는 기호 바로 앞이면 한 번 더 누른 것은 "그만 굵게" 다 — 기호를 넘어간다.
    // 새 짝을 넣으면 `*굵게***` 처럼 기호가 남는다.
    if (text.slice(end, end + n) === mark) {
      const open = line.slice(0, start - head).lastIndexOf(mark)
      if (open >= 0 && head + open + n < start) return { text, start: end + n, end: end + n }
    }
    // 굵은 낱말 한가운데서 누르면 그 짝을 벗긴다 — 슬랙·문서 편집기에서 서식을 끄는 손버릇이다.
    const around = pair_around(line, start - head, mark)
    if (around !== null) {
      const [open, close, k] = around
      const o = head + open
      const c = head + close
      return {
        text: text.slice(0, o) + text.slice(o + k, c - k) + text.slice(c),
        start: start - k,
        end: start - k,
      }
    }
  }
  return {
    text: text.slice(0, start) + mark + picked + mark + text.slice(end),
    start: start + n,
    end: end + n,
  }
}

/** 줄 머리(인용·목록 기호·들여쓰기)와 알맹이, 꼬리 공백. 서식은 알맹이에만 두른다. */
const LINE_PARTS = /^(\s*(?:>\s?)?\s*(?:(?:[-*•]|\d+[.)])\s+)?)(.*?)(\s*)$/

/**
 * 여러 줄에 걸친 선택은 **줄마다** 두른다 — 짝 기호는 줄을 넘지 못하므로(`pair_re`) 통째로
 * 두르면 아무 줄도 굵어지지 않는다. 목록·인용 기호는 기호 밖에 둔다. 모든 줄이 이미 둘러져
 * 있으면 벗긴다.
 */
function wrap_lines(text: string, start: number, end: number, mark: string): Edit {
  const from = text.lastIndexOf('\n', start - 1) + 1
  const nl = text.indexOf('\n', end)
  const to = nl < 0 ? text.length : nl
  const parts = text
    .slice(from, to)
    .split('\n')
    .map((l) => LINE_PARTS.exec(l) ?? ['', l, '', ''])
  const body = (p: RegExpExecArray | string[]) => p[2] ?? ''
  const live = parts.filter((p) => body(p) !== '' && !FENCE.test(body(p)))
  const all = live.length > 0 && live.every((p) => wrapped_by(body(p), mark) > 0)
  const block = parts
    .map((p) => {
      const [, lead = '', inner = '', trail = ''] = p
      if (inner === '' || FENCE.test(inner)) return `${lead}${inner}${trail}`
      const k = wrapped_by(inner, mark)
      if (all) return `${lead}${inner.slice(k, -k)}${trail}`
      return k > 0 ? `${lead}${inner}${trail}` : `${lead}${mark}${inner}${mark}${trail}`
    })
    .join('\n')
  return {
    text: text.slice(0, from) + block + text.slice(to),
    start: from,
    end: from + block.length,
  }
}

function fence(e: Edit): Edit {
  const { text, start, end } = e
  const before = text.slice(0, start)
  const lead = before.length === 0 || before.endsWith('\n') ? '' : '\n'
  const open = `${lead}\`\`\`\n`
  const picked = text.slice(start, end)
  const tail = text.slice(end)
  const close = `\n\`\`\`${tail.length === 0 || tail.startsWith('\n') ? '' : '\n'}`
  return {
    text: before + open + picked + close + tail,
    start: start + open.length,
    end: start + open.length + picked.length,
  }
}

const LIST_HEAD = /^\s*(?:[-*•]|\d+[.)])\s+/

function prefix_lines(e: Edit, f: Format): Edit {
  const { text, start, end } = e
  const from = text.lastIndexOf('\n', start - 1) + 1
  const nl = text.indexOf('\n', end)
  const to = nl < 0 ? text.length : nl
  const lines = text.slice(from, to).split('\n')
  const has = (l: string) =>
    f === 'quote' ? QUOTE.test(l) : f === 'bullet' ? BULLET.test(l) : NUMBERED.test(l)
  // 목록끼리는 서로 갈아 끼운다 — 점 목록을 번호 목록으로 바꾸면 `1. - 가` 가 아니라 `1. 가` 다.
  const strip = (l: string) => (f === 'quote' ? l.replace(/^>\s?/, '') : l.replace(LIST_HEAD, ''))
  // 여러 줄을 고르면 사이의 빈 줄은 건너뛴다 — 빈 항목(`- `)을 만들지 않고 번호도 세지 않는다.
  const skip = (l: string) => lines.length > 1 && l.trim() === ''
  const all = lines.filter((l) => !skip(l)).every(has)
  let n = 0
  const next = lines.map((l) => {
    if (skip(l)) return l
    if (all)
      return f === 'quote'
        ? strip(l)
        : l.replace(f === 'bullet' ? /^\s*[-*•]\s+/ : /^\s*\d+[.)]\s+/, '')
    n += 1
    return `${f === 'quote' ? '> ' : f === 'bullet' ? '- ' : `${n}. `}${strip(l)}`
  })
  const block = next.join('\n')
  const out = text.slice(0, from) + block + text.slice(to)
  // 커서만 있었으면(한 줄) 커서를 그 줄에서 같은 글자 뒤에 둔다 — 줄 전체를 고른 채 두면 이어 치는
  // 첫 글자가 방금 붙인 `- ` 를 덮어쓴다(3차 J5: ⌘⇧8 → "가" 가 "- 가" 가 아니라 "가").
  if (start === end) {
    const at = Math.max(from, start + (next[0] ?? '').length - (lines[0] ?? '').length)
    return { text: out, start: at, end: at }
  }
  return { text: out, start: from, end: from + block.length }
}

/** 시프트를 누르면 `key` 가 기호로 바뀐다(⇧7 → &). 그래서 자판 위치(`code`)로 본다. */
const SHIFTED: Readonly<Record<string, Format>> = {
  KeyX: 'strike',
  KeyC: 'code',
  Digit9: 'quote',
  Digit8: 'bullet',
  Digit7: 'numbered',
}
/** `code` 를 주지 않는 환경(옛 브라우저·시험)용. */
const SHIFTED_KEY: Readonly<Record<string, Format>> = {
  x: 'strike',
  c: 'code',
  '(': 'quote',
  '*': 'bullet',
  '&': 'numbered',
}
const PLAIN: Readonly<Record<string, Format>> = { b: 'bold', i: 'italic' }

/**
 * 단축키 → 서식. 슬랙 도움말(201374536 "Format messages")과 같은 조합이다.
 * `mod` 는 맥의 ⌘, 그 밖의 Ctrl.
 */
export function format_of_key(k: {
  key: string
  code?: string
  mod: boolean
  shift: boolean
  alt: boolean
}): Format | null {
  if (!k.mod) return null
  const key = k.key.toLowerCase()
  const code = k.code ?? ''
  if (!k.shift) return k.alt ? null : (PLAIN[key] ?? null)
  if (k.alt) return code === 'KeyC' || key === 'c' ? 'code_block' : null
  return SHIFTED[code] ?? SHIFTED_KEY[key] ?? null
}

const ITEM = /^(\s*)([-*•]|(\d+)([.)]))\s+(.*)$/
const QUOTED = /^(>\s?)(.*)$/

/**
 * 목록·인용 줄에서 줄을 바꾼다. 다음 줄에 같은 기호를 이어 주고, 번호는 하나 올린다.
 * 빈 항목에서 또 바꾸면 기호를 떼어 목록을 끝낸다 — 슬랙·문서 편집기와 같은 손버릇이다.
 * 목록 줄이 아니거나, 선택이 있거나, 커서가 기호 안·앞이면 `null`(평범한 줄바꿈) — 기호 앞에서
 * 바꾸면 줄이 통째로 내려가야지 `- - 가` 가 되면 안 된다.
 */
export function continue_list(e: Edit, quote = true): Edit | null {
  if (e.start !== e.end) return null
  const head = e.text.lastIndexOf('\n', e.start - 1) + 1
  const tail_at = e.text.indexOf('\n', e.start)
  const tail = tail_at < 0 ? e.text.length : tail_at
  const line = e.text.slice(head, tail)
  const item = ITEM.exec(line)
  const quoted = item === null && quote ? QUOTED.exec(line) : null
  const content = item?.[5] ?? quoted?.[2]
  if (content === undefined) return null
  if (e.start - head < line.length - content.length) return null
  if (content.trim() === '') {
    const text = `${e.text.slice(0, head)}${e.text.slice(tail)}`
    return { text, start: head, end: head }
  }
  let marker = quoted?.[1] ?? ''
  if (item !== null) {
    const [, indent = '', bullet = '-', num, dot] = item
    marker = `${indent}${num === undefined ? bullet : `${Number(num) + 1}${dot}`} `
  }
  const piece = `\n${marker}`
  const text = `${e.text.slice(0, e.start)}${piece}${e.text.slice(e.start)}`
  const at = e.start + piece.length
  return { text, start: at, end: at }
}

/** 커서가 닫지 않은 울타리(```) 안인가. 그 안의 Enter 는 보내기가 아니라 줄바꿈이다. */
export function in_fence(text: string, at: number): boolean {
  let fenced = false
  // 커서 줄은 통째로 본다 — 한 줄 울타리(```코드```) 한가운데는 울타리 안이 아니다.
  const nl = text.indexOf('\n', at)
  for (const raw of text.slice(0, nl < 0 ? text.length : nl).split('\n')) {
    const bare = raw.trim()
    if (fenced ? bare === '```' : FENCE.test(bare) && !/^```(.+)```$/.test(bare)) fenced = !fenced
  }
  return fenced
}

/**
 * 커서 자리에 켜져 있는 서식 — 서식 줄의 단추가 눌린 채로 보인다(슬랙과 같다). 거울(`marks_of`)과
 * 같은 규칙이라 화면이 굵게 보이면 단추도 눌려 있다. 기호 바로 뒤(`*굵게*|`)는 서식 밖이다.
 */
export function formats_at(text: string, at: number): Format[] {
  const head = text.lastIndexOf('\n', at - 1) + 1
  const row = text.slice(0, head).split('\n').length - 1
  const line = marks_of(text)[row]
  if (line === undefined) return []
  const out: Format[] = []
  if (line.kind === 'code' || line.kind === 'fence') return in_fence(text, at) ? ['code_block'] : []
  if (line.kind !== 'plain') out.push(line.kind)
  const col = at - head
  let off = 0
  for (const p of line.pieces) {
    const next = off + p.text.length
    if (!p.mark && col > off && col <= next) {
      if (p.bold) out.push('bold')
      if (p.italic) out.push('italic')
      if (p.strike) out.push('strike')
      if (p.code) out.push('code')
      break
    }
    off = next
  }
  return out
}

/**
 * 입력칸의 Enter. `'send'` 는 보내기, `Edit` 는 그 글로 갈아 끼우기, `null` 은 평범한 줄바꿈(브라우저에
 * 맡긴다). 슬랙과 같은 손버릇이다 — Enter 는 보내기, Shift+Enter 는 줄바꿈, ⌘/Ctrl+Enter 는 어디서든
 * 보내기. 목록 줄의 Enter 는 다음 항목을 잇고(빈 항목이면 목록을 끝낸다), 인용은 Shift+Enter 로만
 * 잇는다. 닫지 않은 울타리(```) 안의 Enter 는 줄바꿈이다 — 코드를 쓰다 반쯤 날아가지 않게.
 */
export function on_enter(
  e: Edit,
  k: { shift: boolean; mod: boolean; alt: boolean },
): Edit | 'send' | null {
  if (k.mod) return 'send'
  if (k.alt) return k.shift ? null : 'send'
  if (!k.shift && in_fence(e.text, e.start)) return null
  return continue_list(e, k.shift) ?? (k.shift ? null : 'send')
}
